import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { FiAward, FiMapPin, FiCalendar, FiGlobe, FiStar, FiChevronDown, FiX, FiPlay, FiUsers, FiTrendingUp, FiShield, FiZap, FiArrowRight } from "react-icons/fi";
import { FaArrowRight, FaCrown } from "react-icons/fa";
const gpu = { willChange:"transform,opacity", transform:"translateZ(0)", backfaceVisibility:"hidden" };

const vp = (s,e) => ({ hidden:{ opacity:0, y:s }, show:{ opacity:1, y:0, transition:{ duration:0.85, ease:[0.22,1,0.36,1] } } });
const stagger = { hidden:{}, show:{ transition:{ staggerChildren:0.11 } } };
const fadeUp = vp(50,0);

// Magnetic hover card
function MagCard({ children, className="", style={} }) {
  const ref = useRef(null);
  const x = useMotionValue(0); const y = useMotionValue(0);
  const sx = useSpring(x,{stiffness:200,damping:20});
  const sy = useSpring(y,{stiffness:200,damping:20});
  const handleMove = e => {
    if(!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set(((e.clientX-r.left)/r.width-0.5)*18);
    y.set(((e.clientY-r.top)/r.height-0.5)*18);
  };
  return (
    <motion.div ref={ref} onMouseMove={handleMove} onMouseLeave={()=>{x.set(0);y.set(0);}}
      style={{ rotateX:sy, rotateY:sx, transformStyle:"preserve-3d", transformPerspective:800, ...style }}
      className={className}>
      {children}
    </motion.div>
  );
}

// ══════════════════════════════════════════
// 3D tilting gallery card with cursor spotlight + rotating gradient glow border
// ══════════════════════════════════════════
function GalleryCard({ img, index, onClick }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 260, damping: 22 });
  const sry = useSpring(ry, { stiffness: 260, damping: 22 });
  const [glow, setGlow] = useState({ x: 50, y: 50 });

  const handleMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    ry.set((px - 0.5) * 22);
    rx.set((py - 0.5) * -22);
    setGlow({ x: px * 100, y: py * 100 });
  };
  const handleLeave = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div variants={fadeUp} className="relative break-inside-avoid mb-5">
      <motion.div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onClick={onClick}
        style={{ rotateX: srx, rotateY: sry, transformPerspective: 1000, transformStyle: "preserve-3d" }}
        whileHover={{ scale: 1.035 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="relative cursor-pointer group"
      >
        {/* animated conic gradient glow border */}
        <motion.div
          className="absolute -inset-[3px] rounded-[32px] opacity-0 group-hover:opacity-100 pointer-events-none"
          style={{
            background: "conic-gradient(from 0deg, #F29A0C, #FFD166, #6B8A5A, #F29A0C)",
            filter: "blur(10px)",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        />

        <div className="relative rounded-[28px] overflow-hidden border-2 border-[#F29A0C]/70 shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
          {/* image */}
          <img
            src={img.src}
            alt={img.label}
            style={{ willChange: "transform" }}
            className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.14] group-hover:brightness-[0.82] block"
          />

          {/* cursor spotlight */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
            style={{
              background: `radial-gradient(circle at ${glow.x}% ${glow.y}%, rgba(255,209,102,0.4), transparent 45%)`,
              mixBlendMode: "overlay",
            }}
          />

          {/* diagonal shine sweep */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -inset-y-10 -left-1/2 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-[200%] group-hover:translate-x-[450%] transition-transform duration-[1100ms] ease-out" />
          </div>

          {/* top sweep line */}
          <motion.div
            className="absolute top-0 left-0 right-0 h-[3px] origin-left"
            style={{ background: "linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }}
            initial={{ scaleX: 0 }}
            whileHover={{ scaleX: 1 }}
            transition={{ duration: 0.5 }}
          />

          {/* bottom overlay */}
          <div
            className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-all duration-500"
            style={{ background: "linear-gradient(to top,rgba(10,5,0,0.9) 0%,rgba(10,5,0,0.4) 50%,transparent 100%)" }}
          >
            <div
              className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[1.5px] translate-y-3 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
              style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", color: "#fff", boxShadow: "0 5px 20px rgba(242,154,12,0.5)" }}
            >
              {img.tag}
            </div>

            <div
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center scale-0 group-hover:scale-100 transition-all duration-500"
              style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.25)" }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1h5M1 1v5M13 13H8M13 13V8M13 1h-5M13 1v5M1 13h5M1 13V8" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>

            <div className="translate-y-4 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 delay-75">
              <p className="font-black text-white text-[16px]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                {img.label}
              </p>
              <p className="text-[12px] mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>
                Divya Nithin Bhojwani · World Championship 2024
              </p>
            </div>
          </div>

          {index === 0 && (
            <div className="absolute inset-x-0 bottom-0 h-[3px]" style={{ background: "linear-gradient(90deg,transparent,#F29A0C,transparent)" }} />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Gallery images   high quality yoga/championship
const GALLERY = [
  { src:"/assets/achievement4.jpeg",  label:"Gold Medal Moment",        tag:"🥇 Gold" },
  { src:"/assets/achievement2.jpeg",  label:"Championship Performance",  tag:"Masters" },
  { src:"/assets/achievement3.jpeg",           tag:"World Stage" },
  { src:"/assets/achievement1.jpeg",  label:"Award Ceremony",            tag:"Ahmedabad" },
  { src:"/assets/achievement5.jpeg",  label:"India's Pride",             tag:"2026" },
  { src:"https://images.pexels.com/photos/4056723/pexels-photo-4056723.jpeg",  label:"Podium Moment",             tag:"1st Place" },
];

const COUNTRIES = [
  "🇳🇿 New Zealand","🇷🇴 Romania","🇳🇱 Netherlands","🇴🇲 Oman","🇺🇿 Uzbekistan",
  "🇺🇸 United States","🇧🇼 Botswana","🇮🇹 Italy","🇸🇨 Seychelles","🇦🇺 Australia",
  "🇨🇦 Canada","🇯🇵 Japan","🇩🇪 Germany","🇧🇷 Brazil","🇫🇷 France",
  "🇿🇦 South Africa","🇦🇪 UAE","🇸🇬 Singapore","🇲🇾 Malaysia","🇬🇧 United Kingdom",
];

const DIGS = ["Dr. Jaydev Arya","Dr. Sanjay Malpani","Dr. Anand Balyogi","Dr. Aarti Pal","Dr. C. V. Jayanti","Rohit Kaushik"];

export default function AchievementsPage() {
  const [lightbox, setLightbox] = useState(null);
  const [isMobile, setIsMobile] = useState(true);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => setShouldLoadVideo(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target:heroRef, offset:["start start","end start"] });
  const heroY  = useTransform(scrollYProgress,[0,1],["0%","25%"]);
  const heroOp = useTransform(scrollYProgress,[0,0.65],[1,0]);
  const heroScale = useTransform(scrollYProgress,[0,1],[1,1.08]);
   const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const smoothRotateX = useSpring(rotateX, { stiffness: 150, damping: 20 });
  const smoothRotateY = useSpring(rotateY, { stiffness: 150, damping: 20 });
 
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    rotateX.set(dy * -6);
    rotateY.set(dx * 6);
  };
 
  return (
    <div style={{ background:"#FFF8EE", fontFamily:"'Inter',sans-serif", overflowX:"hidden" }}>

      {/* ══════════════════════════════════════════
          HERO   Full video/parallax, cinematic
      ══════════════════════════════════════════ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">

        {/* Parallax background image */}
        <motion.div style={{ y:heroY, scale:heroScale, ...gpu }} className="absolute inset-0 w-full min-h-screen">
          {!isMobile && shouldLoadVideo ? (
            <video autoPlay muted loop playsInline preload="auto"
              alt="" className="w-full h-full object-cover"
              style={{ filter:"saturate(0.55) brightness(0.38)" }}>
                  <source src="/assets/video17.mp4" type="video/mp4" />
            </video>
          ) : (
            <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage:"url('https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&q=70')", filter:"saturate(0.55) brightness(0.38)" }} />
          )}
        </motion.div>

        {/* Deep dark vignette */}
        <div className="absolute inset-0" style={{ background:"linear-gradient(to bottom,rgba(10,5,0,0.55) 0%,rgba(10,5,0,0.3) 40%,rgba(10,5,0,0.82) 100%)" }}/>
        <div className="absolute inset-0" style={{ background:"radial-gradient(ellipse at 60% 40%,rgba(242,154,12,0.12),transparent 65%)" }}/>

        {/* Animated grain texture */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{ backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundRepeat:"repeat", backgroundSize:"128px 128px" }}/>

        {/* Gold horizontal line top */}
        <div className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background:"linear-gradient(90deg,transparent 0%,#F29A0C 30%,#FFD166 50%,#F29A0C 70%,transparent 100%)" }}/>

        {/* Floating orbs */}
        <motion.div animate={{ y:[0,-40,0],x:[0,20,0],opacity:[0.3,0.6,0.3] }} transition={{ duration:9, repeat:Infinity, ease:"easeInOut" }}
          className="absolute top-[-100px] right-[-50px] w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.18),transparent 65%)", ...gpu }}/>
        <motion.div animate={{ y:[0,30,0],x:[0,-15,0],opacity:[0.15,0.3,0.15] }} transition={{ duration:13, repeat:Infinity, ease:"easeInOut" }}
          className="absolute bottom-[-80px] left-[-60px] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.25),transparent 65%)", ...gpu }}/>

        {/* Hero content */}
        <motion.div style={{ opacity:heroOp, ...gpu }}
          className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-16 py-14 mt-28">
          <motion.div variants={stagger} initial="hidden" animate="show">

            {/* Live badge */}
            <motion.div variants={fadeUp}
              className="inline-flex items-center gap-3 rounded-full px-5 py-2.5 mb-8 border"
              style={{ background:"rgba(242,154,12,0.12)", borderColor:"rgba(242,154,12,0.35)", backdropFilter:"blur(20px)" }}>
              <motion.div animate={{ scale:[1,1.7,1],opacity:[1,0.4,1] }} transition={{ duration:1.8, repeat:Infinity }}
                className="w-2 h-2 rounded-full" style={{ background:"#F29A0C" }}/>
              <span className="text-[11px] font-black uppercase tracking-[3.5px]" style={{ color:"#ffffff" }}>
                1st World Yogasana Championship · Ahmedabad · June 6, 2024
              </span>
            </motion.div>

            {/* Main headline */}
            <motion.div variants={fadeUp}>
              <h1 className="font-black text-white leading-[1.2] tracking-[2px]  mb-6"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(52px,6.5vw,80px)", letterSpacing:"-3px" }}>
                Gondia's 
                <span style={{
                  background:"linear-gradient(135deg,#F29A0C,#FFD166,#F29A0C)",
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                  backgroundClip:"text", filter:"drop-shadow(0 0 40px rgba(242,154,12,0.5))"
                }}> Daughter.</span><br/>
                <span className="text-white" style={{ fontSize:"0.55em", letterSpacing:"-1px", fontWeight:400, opacity:0.7 }}>
                  India's Gold.
                </span>
              </h1>
            </motion.div>

            <motion.p variants={fadeUp}
              className="text-[18px] leading-[1.9] mb-10 max-w-[600px]"
              style={{ color:"rgba(255,255,255,0.68)" }}>
              Divya Nithin Bhojwani wins the <span style={{ color:"#FFD166", fontWeight:700 }}>Gold Medal</span> at the
              1st International World Yogasana Championship, outperforming participants
              from over <span style={{ color:"#FFD166", fontWeight:700 }}>70 countries</span>.
            </motion.p>

            {/* Stat pills */}
            <motion.div variants={stagger} className="flex flex-wrap gap-3 mb-10">
              {[["🥇","Gold Medal"],["35.42","Points"],["70+","Countries"],["Masters","45–55 Yrs"]].map(([v,l])=>(
                <motion.div key={l} variants={fadeUp} whileHover={{ y:-4, scale:1.05 }}
                  className="flex items-center gap-2.5 px-6 py-3 rounded-full border cursor-default"
                  style={{ background:"rgba(255,255,255,0.07)", borderColor:"rgba(255,255,255,0.15)", backdropFilter:"blur(20px)" }}>
                  <span className="font-black text-[15px]" style={{ color:"#F29A0C" }}>{v}</span>
                  <span className="text-[13px]" style={{ color:"rgba(255,255,255,0.55)" }}>{l}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.div variants={fadeUp} className="flex gap-4 flex-wrap">
              <motion.button whileHover={{ y:-4, scale:1.03 }} whileTap={{ scale:0.97 }}
                className="h-[58px] px-8 rounded-full text-white font-bold flex items-center gap-3 text-[15px] border-none cursor-pointer"
                style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)", boxShadow:"0 20px 60px rgba(242,154,12,0.4)", fontFamily:"'Inter',sans-serif" }}>
                View Full Story <FiArrowRight/>
              </motion.button>
              <motion.button whileHover={{ y:-4 }} whileTap={{ scale:0.97 }}
                className="h-[58px] px-8 rounded-full font-semibold flex items-center gap-3 text-[15px] border cursor-pointer"
                style={{ background:"rgba(255,255,255,0.08)", borderColor:"rgba(255,255,255,0.2)", color:"#fff", backdropFilter:"blur(16px)", fontFamily:"'Inter',sans-serif" }}>
                <FiPlay size={16}/> Watch Highlights
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div animate={{ y:[0,12,0] }} transition={{ duration:2.2, repeat:Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="w-[1px] h-12" style={{ background:"linear-gradient(to bottom,transparent,rgba(242,154,12,0.6))" }}/>
          <FiChevronDown size={16} style={{ color:"rgba(242,154,12,0.7)" }}/>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════
          ACHIEVEMENT + VIDEO   with bg image
      ══════════════════════════════════════════ */}
      <section className="relative py-28 px-6 lg:px-24 overflow-hidden">

        {/* Background image with heavy overlay */}
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/4056723/pexels-photo-4056723.jpeg"
            alt="" className="w-full h-full object-cover"
            style={{ filter:"saturate(0.2) brightness(0.12)" }}/>
          <div className="absolute inset-0" style={{ background:"linear-gradient(135deg,#FFF8EE 0%,rgba(255,248,238,0.97) 50%,rgba(255,244,230,0.95) 100%)" }}/>
        </div>

        {/* Orbs */}
        <motion.div animate={{ y:[0,-25,0] }} transition={{ duration:11, repeat:Infinity, ease:"easeInOut" }}
          className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent)", ...gpu }}/>
        <motion.div animate={{ y:[0,20,0] }} transition={{ duration:14, repeat:Infinity, ease:"easeInOut" }}
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.06),transparent)", ...gpu }}/>

        <div className="relative z-10 max-w-[1440px] mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once:true, amount:0.1 }}>

            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 rounded-full px-5 py-2 mb-5 border"
                style={{ background:"rgba(242,154,12,0.08)", borderColor:"rgba(242,154,12,0.2)" }}>
                <FiAward size={13} style={{ color:"#F29A0C" }}/>
                <span className="text-[11px] font-black uppercase tracking-[3px]" style={{ color:"#E97818" }}>Achievement Summary</span>
              </div>
              <h2 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(32px,4vw,58px)", letterSpacing:"-1.5px" }}>
                A Historic Victory<br/>
                <span style={{ color:"#F29A0C" }}>for India</span>
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">

              {/* Achievement card   glass */}
              <motion.div variants={fadeUp}>
                <MagCard className="relative overflow-hidden rounded-[42px] border border-white/70 bg-white/65 backdrop-blur-3xl shadow-[0_40px_120px_rgba(242,154,12,0.10)]">
                  <div className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background:"linear-gradient(90deg,transparent,#F29A0C,#FFD166,#F29A0C,transparent)" }}/>
                  <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full"
                    style={{ background:"radial-gradient(circle,rgba(242,154,12,0.06),transparent)" }}/>

                  <div className="p-8 lg:p-10">
                    {/* Header */}
                    <div className="flex items-center gap-5 mb-8 pb-8 border-b border-[#EFE5D8]">
                      <motion.div animate={{ rotate:[0,5,-5,0] }} transition={{ duration:5, repeat:Infinity }}
                        className="relative w-[80px] h-[80px] rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background:"conic-gradient(from 0deg,#F29A0C,#FFD166,#E97818,#FFD166,#F29A0C)", boxShadow:"0 0 50px rgba(242,154,12,0.6), 0 0 0 4px rgba(242,154,12,0.15)" }}>
                        <div className="w-[62px] h-[62px] rounded-full flex items-center justify-center" style={{ background:"#FFF8EE" }}>
                          <span style={{ fontSize: "34px" }}>🥇</span>
                        </div>
                      </motion.div>
                      <div>
                        <h3 className="font-black text-[#1F1F1F] text-[22px]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>
                          Divya Nithin Bhojwani
                        </h3>
                        <p className="text-[13px] font-semibold mt-1" style={{ color:"#6B8A5A" }}>
                          Gold Medalist · World Yogasana Champion 2024
                        </p>
                        <div className="flex items-center gap-1.5 mt-2">
                          {[1,2,3,4,5].map(i=><FiStar key={i} size={11} style={{ color:"#F29A0C", fill:"#F29A0C" }}/>)}
                          <span className="text-[11px] font-bold ml-1" style={{ color:"#F29A0C" }}>World #1</span>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-0">
                      {[
                        [FiAward,"#F29A0C","Event","1st World Yogasana Championship"],
                        [FiCalendar,"#6B8A5A","Date","6 June 2024"],
                        [FiMapPin,"#F29A0C","Venue","Ahmedabad, Gujarat, India"],
                        [FiStar,"#6B8A5A","Category","Masters (45 to 55 Years)"],
                        [FiTrendingUp,"#F29A0C","Score","35.42 Points"],
                        [FiAward,"#6B8A5A","Achievement","Gold Medal · 1st Place"],
                        [FiGlobe,"#F29A0C","Represented","India 🇮🇳"],
                      ].map(([Icon,color,label,value])=>(
                        <motion.div key={label} whileHover={{ x:6, background:"rgba(242,154,12,0.04)" }}
                          className="flex items-center gap-4 py-3.5 px-2 rounded-[12px] border-b transition-colors"
                          style={{ borderColor:"rgba(239,229,216,0.7)" }}>
                          <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                            style={{ background:`${color}18` }}>
                            <Icon size={15} style={{ color }}/>
                          </div>
                          <div className="flex-1">
                            <span className="text-[10px] font-black uppercase tracking-[1.5px]" style={{ color:"#AAA" }}>{label}</span>
                            <p className="font-semibold text-[14px] text-[#1F1F1F] mt-0.5">{value}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="mt-6 rounded-[20px] p-5"
                      style={{ background:"linear-gradient(135deg,rgba(242,154,12,0.08),rgba(107,138,90,0.05))", border:"1px solid rgba(242,154,12,0.15)" }}>
                      <p className="text-[13px] leading-[1.75]" style={{ color:"#555" }}>
                        <strong style={{ color:"#1F1F1F" }}>Significance: </strong>
                        Competed against participants from over 70 countries and secured India's top position
                        in her category, bringing international recognition to Gondia and India.
                      </p>
                    </div>
                  </div>
                </MagCard>
              </motion.div>

              {/* RIGHT   Video + Podium */}
              <motion.div variants={fadeUp} className="flex flex-col gap-6">

                {/* YouTube VIDEO */}
                <div className="relative overflow-hidden rounded-[42px] border border-white/70 bg-white/65 backdrop-blur-3xl shadow-[0_40px_120px_rgba(0,0,0,0.08)]">
                  <div className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background:"linear-gradient(90deg,transparent,#F29A0C,transparent)" }}/>
                  {/* Header */}
                  <div className="px-7 pt-6 pb-4 flex items-center gap-3 border-b border-[#EFE5D8]">
                    <div className="w-9 h-9 rounded-[12px] flex items-center justify-center"
                      style={{ background:"linear-gradient(135deg,#FF0000,#CC0000)" }}>
                      <FiPlay size={14} style={{ color:"#fff" }}/>
                    </div>
                    <div>
                      <p className="font-black text-[#1F1F1F] text-[15px]">Championship Highlights</p>
                      <p className="text-[12px]" style={{ color:"#999" }}>World Yogasana Championship · Ahmedabad 2024</p>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full"
                      style={{ background:"rgba(255,0,0,0.08)", border:"1px solid rgba(255,0,0,0.15)" }}>
                      <motion.div animate={{ opacity:[1,0,1] }} transition={{ duration:1.5, repeat:Infinity }}
                        className="w-1.5 h-1.5 rounded-full bg-red-500"/>
                      <span className="text-[10px] font-black uppercase tracking-[1px] text-red-500">Live Replay</span>
                    </div>
                  </div>
                  {/* Embed */}
                  <div className="relative w-full" style={{ paddingBottom:"56.25%", background:"#0A0500" }}>
                    <iframe className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/0H67f5VFGio?si=psTtd1tFmSgXX9ek"
                      title="World Yogasana Championship Highlights"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen/>
                  </div>
                </div>

                {/* Podium glass card */}
                <MagCard className="relative overflow-hidden rounded-[42px] border border-white/80 bg-white/70 backdrop-blur-3xl shadow-[0_30px_90px_rgba(242,154,12,0.08)] hover:shadow-[0_45px_110px_rgba(242,154,12,0.15)] transition-shadow duration-700">
                  {/* Subtle internal gold gradient border */}
                  <div className="absolute top-0 left-0 right-0 h-[4px]"
                    style={{ background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #F29A0C, transparent)" }} />
                  
                  {/* Glowing background aurorals */}
                  <div className="absolute -right-24 -top-24 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.15)_0%,transparent_70%)] blur-2xl pointer-events-none" />
                  <div className="absolute -left-24 -bottom-24 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(107,138,90,0.1)_0%,transparent_70%)] blur-2xl pointer-events-none" />

                  <div className="p-8 relative" style={{ transformStyle: "preserve-3d" }}>
                    <p className="text-[11px] font-black uppercase tracking-[3px] mb-4 text-[#E97818]" style={{ transform: "translateZ(15px)" }}>
                      Championship Results · Masters Category (45–55 Years)
                    </p>
                    
                    {/* The 3D Podium Container */}
                    <div className="relative mt-20 mb-6" style={{ transformStyle: "preserve-3d" }}>
                      <div className="flex items-end justify-center gap-4 sm:gap-6 relative z-20" style={{ transformStyle: "preserve-3d" }}>
                        {[
                          { pos: "2nd", country: "Romania", flagUrl: "https://flagcdn.com/w80/ro.png", color: "#B8B8B8", h: 130, pts: "34.15 pts" },
                          { pos: "1st", country: "India", flagUrl: "https://flagcdn.com/w80/in.png", color: "#F29A0C", h: 170, pts: "35.42 pts", isWinner: true },
                          { pos: "3rd", country: "New Zealand", flagUrl: "https://flagcdn.com/w80/nz.png", color: "#CD7F32", h: 110, pts: "33.80 pts" },
                        ].map((p, i) => (
                          <motion.div key={p.pos}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="flex-1 flex flex-col items-center relative pt-10"
                            style={{ transformStyle: "preserve-3d" }}
                          >
                            {/* Crown for India 1st Place */}
                            {p.isWinner && (
                              <motion.div 
                                animate={{ y: [0, -6, 0] }}
                                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                                className="absolute -top-10 z-30"
                                style={{ transform: "translateZ(60px)" }}
                              >
                                <FaCrown size={60} className="text-[#FFD166] text-[48px] drop-shadow-[0_4px_10px_rgba(242,154,12,0.4)]" />
                              </motion.div>
                            )}

                            {/* Country Flag Sphere sitting on top of Pedestal */}
                            <div 
                              className="relative z-20 flex items-center justify-center -mb-5"
                              style={{ transform: "translateZ(45px)", transformStyle: "preserve-3d" }}
                            >
                              <div 
                                className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center p-1 bg-white/95 border-2 shadow-[0_8px_20px_rgba(0,0,0,0.15)] transition-all duration-300"
                                style={{ 
                                  borderColor: p.color,
                                  boxShadow: `0 0 15px ${p.color}40`
                                }}
                              >
                                <img 
                                  src={p.flagUrl} 
                                  alt={p.country} 
                                  className="w-full h-full object-cover rounded-full" 
                                />
                              </div>
                              {/* Floating Rank Badge */}
                              <div 
                                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black text-white shadow-md"
                                style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}cc)` }}
                              >
                                {p.pos.slice(0, 1)}
                              </div>
                            </div>

                            {/* The Pedestal Pillar Column */}
                            <div 
                              className="w-full rounded-b-[24px] rounded-t-[14px] flex flex-col items-center justify-between pb-4 pt-7 relative overflow-hidden"
                              style={{ 
                                height: `${p.h}px`, 
                                background: p.isWinner 
                                  ? "linear-gradient(180deg, rgba(242,154,12,0.18) 0%, rgba(255,255,255,0.03) 100%)" 
                                  : `linear-gradient(180deg, ${p.color}15 0%, rgba(255,255,255,0.02) 100%)`,
                                border: `1px solid ${p.color}40`, 
                                boxShadow: p.isWinner 
                                  ? "0 20px 45px rgba(242,154,12,0.15), inset 0 1.5px 0 rgba(255,255,255,0.3)" 
                                  : `0 10px 30px ${p.color}08, inset 0 1px 0 rgba(255,255,255,0.25)`,
                                transform: "translateZ(20px)",
                                transformStyle: "preserve-3d"
                              }}
                            >
                              {/* Colored Glow bar at top of pedestal */}
                              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-[14px]"
                                style={{ background: p.color }} />
                              
                              {/* Country Name inside pedestal */}
                              <p className="text-[11px] sm:text-[12px] font-bold text-center text-[#555] tracking-tight mt-1" style={{ transform: "translateZ(25px)" }}>
                                {p.country}
                              </p>

                              {/* Position Text inside pedestal */}
                              <span 
                                className="font-black text-[22px] tracking-tight leading-none my-1" 
                                style={{ 
                                  color: p.color, 
                                  textShadow: p.isWinner ? "0 0 12px rgba(242,154,12,0.4)" : "none",
                                  fontFamily: "'Poppins', sans-serif",
                                  transform: "translateZ(35px)" 
                                }}
                              >
                                {p.pos}
                              </span>
                              
                              {/* Score Tag inside pedestal */}
                              <span 
                                className="text-[9px] font-black tracking-wider px-2.5 py-0.5 rounded-full text-white/95 bg-black/30" 
                                style={{ transform: "translateZ(30px)" }}
                              >
                                {p.pts.split(" ")[0]}
                              </span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Glowing Base Platform under pillars */}
                      <div 
                        className="w-full h-3.5 rounded-full border border-white/40 backdrop-blur-md mt-[-8px] z-10 relative overflow-hidden"
                        style={{ 
                          background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(242,154,12,0.2) 50%, rgba(255,255,255,0.05) 100%)",
                          boxShadow: "0 10px 30px rgba(242,154,12,0.15), inset 0 1px 0 rgba(255,255,255,0.3)",
                          transform: "translateZ(10px)"
                        }}
                      >
                        <div className="absolute inset-x-12 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#F29A0C]/50 to-transparent" />
                      </div>
                    </div>

                    {/* Bottom Winner Card */}
                    <div 
                      className="rounded-[22px] px-5 py-4 flex items-center justify-between mt-10 relative overflow-hidden"
                      style={{ 
                        background: "linear-gradient(90deg, rgba(242,154,12,0.09) 0%, rgba(255,209,102,0.03) 100%)", 
                        border: "1px solid rgba(242,154,12,0.2)",
                        transform: "translateZ(15px)"
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <img 
                          src="https://flagcdn.com/w40/in.png" 
                          alt="India" 
                          className="w-7 h-5 object-cover rounded-[3px] border border-white/50 shadow-sm" 
                        />
                        <span className="text-[14px] font-black text-[#1F1F1F]">Divya Nithin Bhojwani · Gold Medalist</span>
                      </div>
                      
                      <span 
                        className="font-black text-[13px] px-4 py-2 rounded-full text-white tracking-wider uppercase"
                        style={{ 
                          background: "linear-gradient(135deg, #F29A0C, #E97818)", 
                          boxShadow: "0 6px 20px rgba(242,154,12,0.4)" 
                        }}
                      >
                        35.42 pts
                      </span>
                    </div>
                  </div>
                </MagCard>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          GALLERY   3D tilt cards, spotlight, rotating gradient glow
      ══════════════════════════════════════════ */}
      <section className="relative py-14 px-6 lg:px-24 overflow-hidden">

        {/* Full background image */}
        <div className="absolute inset-0">
          <img src="/assets/image 29.jpeg"
            alt="" className="w-full h-full object-cover"
            style={{ filter:"saturate(0.15) brightness(0.08)" }}/>
          <div className="absolute inset-0" style={{ background:"linear-gradient(160deg,rgba(255,244,224,0.98),rgba(255,248,238,0.95))" }}/>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once:true, amount:0.05 }}>

            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 rounded-full px-5 py-2 mb-5 border"
                style={{ background:"rgba(107,138,90,0.08)", borderColor:"rgba(107,138,90,0.2)" }}>
                <FiStar size={13} style={{ color:"#6B8A5A" }}/>
                <span className="text-[11px] font-black uppercase tracking-[3px]" style={{ color:"#6B8A5A" }}>Championship Gallery</span>
              </div>
              <h2 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(32px,4vw,58px)", letterSpacing:"-1.5px" }}>
                Moments of<br/>
                <span style={{ color:"#F29A0C" }}>Glory</span>
              </h2>
              <p className="text-[16px] mt-4 max-w-[480px] mx-auto" style={{ color:"#888", lineHeight:1.85 }}>
                Every pose, every moment captured on the world stage.
              </p>
            </motion.div>

            {/* GALLERY GRID   3D tilt cards */}
            <div className="columns-2 lg:columns-3 gap-5">
              {GALLERY.map((img, i) => (
                <GalleryCard key={i} img={img} index={i} onClick={() => setLightbox(img)} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          STORY + DIGNITARIES   bg image
      ══════════════════════════════════════════ */}
      <section className="relative py-28 px-6 lg:px-24 overflow-hidden">

        {/* Background */}
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/3822906/pexels-photo-3822906.jpeg"
            alt="" className="w-full h-full object-cover"
            style={{ filter:"saturate(0.2) brightness(0.1)" }}/>
          <div className="absolute inset-0" style={{ background:"rgba(255,248,238,0.97)" }}/>
        </div>

        <motion.div animate={{ y:[0,-20,0] }} transition={{ duration:12, repeat:Infinity, ease:"easeInOut" }}
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.06),transparent)", ...gpu }}/>

        <div className="relative z-10 max-w-[1440px] mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once:true, amount:0.08 }}>

            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 rounded-full px-5 py-2 mb-5 border"
                style={{ background:"rgba(242,154,12,0.08)", borderColor:"rgba(242,154,12,0.2)" }}>
                <FiZap size={13} style={{ color:"#F29A0C" }}/>
                <span className="text-[11px] font-black uppercase tracking-[3px]" style={{ color:"#E97818" }}>The Full Story</span>
              </div>
              <h2 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(32px,4vw,58px)", letterSpacing:"-1.5px" }}>
                From Gondia to<br/>
                <span style={{ color:"#F29A0C" }}>the World Stage</span>
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Story */}
              <div className="space-y-5">
                {[
                  "Gondia's renowned yoga trainer Divya Nithin Bhojwani won a Gold Medal at the 1st International World Yogasana Championship, held in Ahmedabad, Gujarat, on 6 June 2024. With this achievement, she brought pride to both India and Gondia on the global stage.",
                  "Divya Nithin Bhojwani competed in the Masters Category (45–55 years age group) and secured first place with 35.42 points. She won the gold medal by outperforming participants from Romania (2nd place) and New Zealand (3rd place).",
                  "The championship was organized jointly under the supervision of Yogasana Bharat, World Yogasana, and the Indian Olympic Association (IOA), and was inaugurated virtually by Prime Minister Narendra Modi.",
                ].map((text, i) => (
                  <motion.div key={i} variants={fadeUp}
                    className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/65 backdrop-blur-3xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[28px]"
                      style={{ background:`linear-gradient(to bottom,${i%2===0?"#F29A0C":"#6B8A5A"},transparent)` }}/>
                    <p className="text-[15px] leading-[1.9]" style={{ color:"#555" }}
                      dangerouslySetInnerHTML={{ __html: text.replace(/(Gold Medal|35\.42 points|Masters Category|Narendra Modi|Gondia)/g,'<strong style="color:#1F1F1F">$1</strong>') }}/>
                  </motion.div>
                ))}

                {/* Organizers */}
                <motion.div variants={fadeUp}
                  className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/65 backdrop-blur-3xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
                  <p className="text-[10px] font-black uppercase tracking-[2.5px] mb-5" style={{ color:"#6B8A5A" }}>Organized By</p>
                  {["Yogasana Bharat","World Yogasana","Indian Olympic Association (IOA)"].map((org,i)=>(
                    <motion.div key={org} whileHover={{ x:6 }}
                      className="flex items-center gap-3 py-3 border-b" style={{ borderColor:"rgba(239,229,216,0.7)" }}>
                      <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0"
                        style={{ background:i%2===0?"rgba(242,154,12,0.1)":"rgba(107,138,90,0.1)" }}>
                        <FiGlobe size={13} style={{ color:i%2===0?"#F29A0C":"#6B8A5A" }}/>
                      </div>
                      <span className="font-semibold text-[14px] text-[#1F1F1F]">{org}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* Quote + dignitaries */}
              <div className="space-y-5">
                {/* Dark quote card */}
                <motion.div variants={fadeUp}
                  className="relative overflow-hidden rounded-[42px] p-9 border"
                  style={{ background:"linear-gradient(145deg,#120700,#1A0D00)", borderColor:"rgba(242,154,12,0.2)", boxShadow:"0 40px_100px_rgba(0,0,0,0.25)" }}>
                  <div className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background:"linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }}/>
                  <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none"
                    style={{ background:"radial-gradient(circle,rgba(242,154,12,0.1),transparent)", filter:"blur(40px)" }}/>
                  <div className="text-[64px] leading-none mb-3" style={{ color:"rgba(242,154,12,0.18)", fontFamily:"Georgia,serif" }}>"</div>
                  <p className="text-[18px] font-semibold leading-[1.8] text-white mb-6">
                    She competed against participants from over{" "}
                    <span style={{ color:"#FFD166" }}>70 countries</span> and secured
                    India's top position, bringing international recognition to{" "}
                    <span style={{ color:"#FFD166" }}>Gondia</span> and India.
                  </p>
                  <div className="flex items-center gap-3 pt-5 border-t" style={{ borderColor:"rgba(255,255,255,0.08)" }}>
                    <div className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)" }}>
                      <FiAward size={18} style={{ color:"#fff" }}/>
                    </div>
                    <div>
                      <p className="font-black text-white text-[14px]">Divya Nithin Bhojwani</p>
                      <p className="text-[11px]" style={{ color:"rgba(255,255,255,0.4)" }}>World Champion · Masters 2024</p>
                    </div>
                  </div>
                </motion.div>

                {/* Dignitaries */}
                <motion.div variants={fadeUp}
                  className="relative overflow-hidden rounded-[32px] border border-white/70 bg-white/65 backdrop-blur-3xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
                  <p className="text-[10px] font-black uppercase tracking-[2.5px] mb-5" style={{ color:"#F29A0C" }}>Congratulated By</p>
                  <div className="grid grid-cols-2 gap-3">
                    {DIGS.map((d,i)=>(
                      <motion.div key={d} whileHover={{ y:-3, scale:1.02, boxShadow:"0 12px 30px rgba(242,154,12,0.1)" }}
                        className="flex items-center gap-3 px-4 py-3 rounded-[16px] border transition-all"
                        style={{ background:"rgba(255,255,255,0.8)", borderColor:"rgba(239,229,216,0.8)" }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[11px] font-black"
                          style={{ background:i%2===0?"linear-gradient(135deg,#F29A0C,#E97818)":"linear-gradient(135deg,#6B8A5A,#8BA47A)" }}>
                          {d.replace("Dr. ","")[0]}
                        </div>
                        <span className="font-semibold text-[12px] text-[#1F1F1F] leading-tight">{d}</span>
                      </motion.div>
                    ))}
                    
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          70+ NATIONS   Dramatic dark with bg image
      ══════════════════════════════════════════ */}
      <section className="relative py-14 px-6 lg:px-24 overflow-hidden">

        {/* Full dramatic background */}
        <div className="absolute inset-0">
          <img src="/assets/image 1.jpeg"
            alt="" className="w-full h-full object-cover"
            style={{ filter:"saturate(0.3) brightness(0.18)", transform:"scale(1.05)" }}/>
          <div className="absolute inset-0" style={{ background:"linear-gradient(145deg,#0A0500 0%,#120900 10%,rgba(18,7,0,0.1) 30%)" }}/>
          <div className="absolute inset-0" style={{ background:"radial-gradient(ellipse at 50% 0%,rgba(242,154,12,0.1),transparent 60%)" }}/>
        </div>
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage:"radial-gradient(circle,rgba(242,154,12,0.09) 1px,transparent 1px)", backgroundSize:"44px 44px", opacity:0.5 }}/>

        <motion.div animate={{ opacity:[0.1,0.25,0.1] }} transition={{ duration:7, repeat:Infinity }}
          className="absolute top-0 right-[-100px] w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.1)_0%,transparent_70%)", ...gpu }}/>

        <div className="relative z-10 max-w-[1300px] mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once:true, amount:0.08 }}>

            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 rounded-full px-5 py-2 mb-5 border"
                style={{ background:"rgba(242,154,12,0.1)", borderColor:"rgba(242,154,12,0.25)" }}>
                <FiGlobe size={13} style={{ color:"#F29A0C" }}/>
                <span className="text-[11px] font-black uppercase tracking-[3px]" style={{ color:"#FFD166" }}>Global Competition</span>
              </div>
              <h2 className="font-black text-white"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:"clamp(32px,4.5vw,64px)", letterSpacing:"-1.5px" }}>
                70+ Nations.<br/>
                <span style={{ color:"#F29A0C" }}>One Champion.</span>
              </h2>
              <p className="text-[16px] mt-4 max-w-[500px] mx-auto" style={{ color:"rgba(255,255,255,0.45)", lineHeight:1.85 }}>
                The world's best yoga practitioners gathered and India stood tallest.
              </p>
            </motion.div>

            <motion.div variants={stagger} className="flex flex-wrap gap-3 justify-center mb-16">
              {COUNTRIES.map((country,i)=>{
                const isIndia = country.includes("India");
                return (
                  <motion.div key={country} variants={fadeUp}
                    whileHover={{ y:-6, scale:1.1 }}
                    className="px-5 py-2.5 rounded-full cursor-default"
                    style={{
                      background:isIndia?"linear-gradient(135deg,rgba(242,154,12,0.3),rgba(233,120,24,0.2))":"rgba(255,255,255,0.06)",
                      border:isIndia?"1px solid rgba(242,154,12,0.55)":"1px solid rgba(255,255,255,0.1)",
                      color:isIndia?"#FFD166":"rgba(255,255,255,0.58)",
                      fontWeight:isIndia?800:500, fontSize:13,
                      boxShadow:isIndia?"0 0 30px rgba(242,154,12,0.25)":"none",
                      transition:"all 0.25s",
                    }}>
                    {isIndia?"🇮🇳 India 🥇":country}
                  </motion.div>
                );
              })}
              <div className="px-5 py-2.5 rounded-full text-[13px]"
                style={{ background:"rgba(255,255,255,0.03)", border:"1px dashed rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.28)" }}>
                + 50 more nations
              </div>
            </motion.div>

            {/* Stat cards */}
            <div className="grid grid-cols-3 gap-5">
              {[
                [FiUsers,"#F29A0C","70+","Nations Participated"],
                [FiAward,"#6B8A5A","Gold","Medal for India"],
                [FiShield,"#F29A0C","#1","India's Rank Masters"],
              ].map(([Icon,color,val,lbl])=>(
                <motion.div key={lbl} variants={fadeUp}
                  whileHover={{ y:-6, scale:1.03 }}
                  className="relative overflow-hidden rounded-[32px] border p-2 py-4 text-center"
                  style={{ background:"rgba(255,255,255,0.04)", borderColor:"rgba(255,255,255,0.08)", backdropFilter:"blur(20px)" }}>
                  <div className="absolute top-0 left-0 right-0 h-[2px]"
                    style={{ background:`linear-gradient(90deg,transparent,${color},transparent)` }}/>
                  <div className="w-11 h-11 rounded-[14px] flex items-center justify-center mx-auto mb-4"
                    style={{ background:`${color}20` }}>
                    <Icon size={20} style={{ color }}/>
                  </div>
                  <p className="font-black text-[38px] leading-none text-center" style={{ fontFamily:"'Poppins',sans-serif", color }}>{val}</p>
                  <p className="text-[13px] mt-2" style={{ color:"rgba(255,255,255,0.4)" }}>{lbl}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/*CTA*/}
    <section className="relative overflow-hidden py-20 bg-[#FFF8EE]">
 
      {/* ── Background   identical structure to About page ── */}
      <div className="absolute inset-0">
        {/* Replace with your achievement bg image */}
        <img
          src="/assets/image 9.jpeg"
          alt="Achievement Background"
          className="w-full h-full object-cover opacity-[0.99]"
        />
 
        {/* Orbs   same positions as About page */}
        <div className="absolute top-[-180px] left-[-120px] w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.1)_0%,transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-[-220px] right-[-150px] w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(255,179,71,0.08)_0%,transparent_70%)] pointer-events-none" />
 
        {/* Warm gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE]/65 via-[#FFF3E0]/50 to-[#FFFFFF]/60" />
 
        {/* Watermark logo   same as About page */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
          <img src="/assets/logo_12.png" alt="" className="w-[620px] object-contain" />
        </div>
      </div>
 
      {/* ── Content wrapper ── */}
      <div className="relative z-20 max-w-[1350px] mx-auto px-6 lg:px-20">
 
        {/* ── Tilt card   identical motion setup ── */}
        <motion.div
          onMouseMove={handleMouseMove}
          onMouseLeave={() => { rotateX.set(0); rotateY.set(0); }}
          style={{
            rotateX: smoothRotateX,
            rotateY: smoothRotateY,
            transformPerspective: 1400,
            transformStyle: "preserve-3d",
            willChange: "transform",
          }}
          className="relative overflow-hidden rounded-[42px] border border-[#F4D9B5] bg-gradient-to-br from-white/10 via-[#FFF8EE]/20 to-[#FFE8C7]/30 backdrop-blur-3xl shadow-[0_30px_100px_rgba(242,154,12,0.08)] px-8 py-12 md:px-14 md:py-14"
        >
          {/* Inner ambient orbs   same as About */}
          <div className="absolute top-[-100px] right-[-60px] w-[240px] h-[240px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.25)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute bottom-[-100px] left-[-60px] w-[240px] h-[240px] rounded-full bg-[radial-gradient(circle,rgba(255,179,71,0.25)_0%,transparent_70%)] pointer-events-none" />
 
          {/* ── Grid   same as About page ── */}
          <div
            style={{ transform: "translateZ(40px)" }}
            className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-14 items-center"
          >
 
            {/* ── LEFT   achievement content ── */}
            <div className="text-left">
 
              {/* Badge with pulse   same style as About */}
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F4D9B5] bg-white/70 px-5 py-3 backdrop-blur-xl">
                <div className="w-2 h-2 rounded-full bg-[#F29A0C] animate-pulse" />
                <span className="text-[#F29A0C] text-[10px] tracking-[4px] uppercase font-semibold">
                  World Yogasana Championship 2024
                </span>
              </div>
 
              {/* Headline   same size & weight as About */}
              <h2
                className="text-[42px] md:text-[68px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-8"
                style={{ fontFamily: "Poppins" }}
              >
                Inspired By Divya's<br />
                <span className="text-[#F29A0C]">Championship Journey?</span>
              </h2>
 
              {/* Description */}
              <p className="text-[#222222] text-[17px] leading-[34px] mt-6 max-w-[700px]">
                Divya Nithin Bhojwani proved that dedication and expert guidance can take
                you to the world stage earning Gold at 70+ nations. Your
                transformation begins with a single session.
              </p>
 
              {/* Tag pills   same style as About */}
              <div className="flex flex-wrap justify-start gap-3 mt-6">
                {["World Champion", "Gold Medal", "35.42 Points", "70+ Nations"].map((item, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -3, scale: 1.04 }}
                    className="rounded-full border border-[#F4D9B5] bg-white/60 backdrop-blur-xl px-5 py-2.5 text-[#444444] text-xs font-medium shadow-[0_8px_30px_rgba(242,154,12,0.04)]"
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
 
              {/* Buttons   same exact style as About */}
              <div className="flex flex-wrap items-center gap-4 mt-10">
                <Link to="/register">
                  <button className="rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8A00] text-white px-8 py-4 text-[14px] font-semibold shadow-[0_20px_60px_rgba(242,154,12,0.25)] hover:scale-105 transition-all duration-500 flex items-center gap-3 cursor-pointer">
                    Begin Your Journey <FaArrowRight />
                  </button>
                </Link>
                <Link to="/courses">
                  <button className="rounded-full border border-[#F4D9B5] bg-white/70 text-[#111111] px-8 py-4 text-[14px] font-semibold hover:bg-[#F29A0C] hover:text-white transition-all duration-500 cursor-pointer">
                    Explore Classes
                  </button>
                </Link>
              </div>
            </div>
 
            {/* ── RIGHT   achievement image, same card as About ── */}
            <div className="relative overflow-hidden rounded-[30px] border border-[#F4D9B5] bg-gradient-to-br from-white/80 to-[#FFF3E0]/80 backdrop-blur-2xl p-2 h-[400px] shadow-[0_20px_60px_rgba(242,154,12,0.08)]">
              {/* Replace with your Divya / championship image */}
              <img
                src="/assets/achievement2.jpeg"
                alt="Divya Nithin Bhojwani Championship"
                className="w-full h-full object-cover rounded-[22px] opacity-[0.95]"
              />
            </div>
 
          </div>
        </motion.div>
      </div>
    </section>

      {/* ══════════════════════════════════════════
          LIGHTBOX   luxury
      ══════════════════════════════════════════ */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={()=>setLightbox(null)}
            className="fixed inset-0 z-[999] flex items-center justify-center p-6"
            style={{ background:"rgba(10,5,0,0.94)", backdropFilter:"blur(28px)" }}>

            <motion.button onClick={()=>setLightbox(null)} whileHover={{ scale:1.1, background:"rgba(242,154,12,0.2)" }}
              className="absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer border"
              style={{ background:"rgba(255,255,255,0.08)", borderColor:"rgba(255,255,255,0.15)", color:"#fff", border:"none" }}>
              <FiX size={20}/>
            </motion.button>

            <motion.div
              initial={{ scale:0.85, opacity:0, y:30 }} animate={{ scale:1, opacity:1, y:0 }}
              exit={{ scale:0.88, opacity:0 }} transition={{ duration:0.45, ease:[0.22,1,0.36,1] }}
              onClick={e=>e.stopPropagation()}
              className="relative w-auto max-w-[90vw] rounded-[32px] overflow-hidden"
              style={{ boxShadow:"0 60px 140px rgba(0,0,0,0.85), 0 0 0 1px rgba(242,154,12,0.25)" }}>
              <div className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background:"linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }}/>
              <img src={lightbox.src} alt={lightbox.label}
              className="block w-auto h-auto max-w-[90vw] max-h-[85vh] object-contain"/>
              <div className="absolute bottom-0 left-0 right-0 p-8"
                style={{ background:"linear-gradient(to top,rgba(10,5,0,0.94),transparent)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[1.5px]"
                    style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)", color:"#fff" }}>{lightbox.tag}</span>
                </div>
                <p className="font-black text-white text-[22px]" style={{ fontFamily:"'Poppins',sans-serif" }}>{lightbox.label}</p>
                <p className="text-[13px] mt-1.5" style={{ color:"rgba(255,255,255,0.45)" }}>
                  Divya Nithin Bhojwani · World Yogasana Championship · Ahmedabad 2024
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}